import React from 'react'
import { cn } from '../../lib/utils'
import { ContextMenuCtx, useContextMenu } from './contextmenu-hook'

export interface ContextMenuProps {
  children: React.ReactNode
  disabled?: boolean
  onOpenChange?: (open: boolean) => void
}

export const ContextMenu: React.FC<ContextMenuProps> = ({ children, disabled, onOpenChange }) => {
  const [open, setOpen] = React.useState(false)
  const [pos, setPos] = React.useState<{ x: number | null; y: number | null }>({ x: null, y: null })

  const openAt = React.useCallback((x: number, y: number) => {
    if (disabled) return
    setPos({ x, y })
    setOpen(true)
    onOpenChange?.(true)
  }, [disabled, onOpenChange])

  const close = React.useCallback(() => {
    setOpen(false)
    onOpenChange?.(false)
  }, [onOpenChange])

  const value = React.useMemo(() => ({ open, x: pos.x, y: pos.y, disabled, openAt, close }), [open, pos, disabled, openAt, close])

  return (
    <ContextMenuCtx.Provider value={value}>{children}</ContextMenuCtx.Provider>
  )
}

export interface ContextMenuTriggerProps extends React.HTMLAttributes<HTMLElement> {
  asChild?: boolean
  rightClick?: boolean // if true, uses onContextMenu event; else uses onClick
  anchor?: 'pointer' | 'element'
  disabled?: boolean
  children: React.ReactNode
}

export const ContextMenuTrigger: React.FC<ContextMenuTriggerProps> = ({
  asChild,
  rightClick,
  anchor = 'pointer',
  disabled: disabledProp,
  children,
  ...rest
}) => {
  const { openAt, disabled } = useContextMenu()
  const ref = React.useRef<HTMLElement | null>(null)

  const handleOpen = (clientX: number, clientY: number, target?: HTMLElement) => {
    if (disabled || disabledProp) return
    if (anchor === 'element' && target) {
      const rect = target.getBoundingClientRect()
      openAt(rect.right + 4, rect.top + rect.height / 2)
    } else {
      openAt(clientX, clientY)
    }
  }

  const childProps: Record<string, (e: React.MouseEvent<HTMLElement>) => void> = {}
  if (rightClick) {
    childProps.onContextMenu = (e: React.MouseEvent<HTMLElement>) => {
      const restProps = rest as { onContextMenu?: (e: React.MouseEvent<HTMLElement>) => void }
      restProps.onContextMenu?.(e)
      e.preventDefault()
      handleOpen(e.clientX, e.clientY, ref.current || (e.currentTarget as HTMLElement))
    }
  } else {
    childProps.onClick = (e: React.MouseEvent<HTMLElement>) => {
      const restProps = rest as { onClick?: (e: React.MouseEvent<HTMLElement>) => void }
      restProps.onClick?.(e)
      handleOpen(e.clientX, e.clientY, ref.current || (e.currentTarget as HTMLElement))
    }
  }

  if (asChild) {
    const only = React.Children.only(children) as React.ReactElement<React.HTMLAttributes<HTMLElement>>
    const enhancedProps = {
      ...only.props,
      ...childProps,
      className: cn(only.props.className, 'cursor-pointer')
    }
    return React.cloneElement(only, enhancedProps)
  }

  return (
    <span ref={ref} {...rest} {...childProps} className={cn(rest.className, 'cursor-pointer')}>
      {children}
    </span>
  )
}

export interface ContextMenuContentProps {
  children: React.ReactNode
  className?: string
  sideOffset?: number
  collisionPadding?: number
}

export const ContextMenuContent: React.FC<ContextMenuContentProps> = ({ children, className, sideOffset = 4, collisionPadding = 8 }) => {
  const { open, x, y, close } = useContextMenu()
  const ref = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    if (!open) return
    const handle = (e: MouseEvent) => {
      if (!(e.target instanceof Node)) return
      if (ref.current && !ref.current.contains(e.target)) close()
    }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    window.addEventListener('mousedown', handle)
    window.addEventListener('keydown', esc)
    return () => { window.removeEventListener('mousedown', handle); window.removeEventListener('keydown', esc) }
  }, [open, close])

  // simple collision handling after mount
  React.useLayoutEffect(() => {
    if (!open || !ref.current) return
    const el = ref.current
    let left = x ?? 0
    let top = y ?? 0
    const rect = el.getBoundingClientRect()
    if (left + rect.width + collisionPadding > window.innerWidth) left = Math.max(collisionPadding, window.innerWidth - rect.width - collisionPadding)
    if (top + rect.height + collisionPadding > window.innerHeight) top = Math.max(collisionPadding, window.innerHeight - rect.height - collisionPadding)
    el.style.left = `${left}px`
    el.style.top = `${top}px`
  }, [open, x, y, collisionPadding])

  if (!open || x == null || y == null) return null

  return (
    <div
      ref={ref}
      className={cn('fixed z-50 min-w-[176px] rounded-lg bg-white shadow-lg ring-1 ring-black/10 py-1 text-sm animate-in fade-in zoom-in-95', className)}
      role="menu"
      style={{ left: x + sideOffset, top: y }}
    >
      {children}
    </div>
  )
}

export const ContextMenuLabel: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={cn('px-2 py-1.5 text-xs uppercase tracking-wide text-foreground/40 select-none', className)} {...props} />
)

export interface ContextMenuItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  closeOnSelect?: boolean
  onSelect?: (e: React.MouseEvent<HTMLButtonElement>) => void
}

export const ContextMenuItem: React.FC<ContextMenuItemProps> = ({ className, children, closeOnSelect = true, onClick, onSelect, ...props }) => {
  const { close } = useContextMenu()
  return (
    <button
      type="button"
      className={cn('w-full text-left px-3 py-2 hover:bg-black/5 active:bg-black/10 font-medium cursor-pointer flex items-center gap-2 disabled:opacity-50', className)}
      role="menuitem"
      onClick={(e) => {
        onClick?.(e)
        onSelect?.(e)
        if (closeOnSelect) close()
      }}
      {...props}
    >
      {children}
    </button>
  )
}
